/**
 * A move that asks a question rather than naming a card.
 *
 * Every input before this was "tap a card" or "press a button that takes no
 * argument", because every move was one of those. Bidding is neither: it
 * carries an argument and the argument is a number, so the hand — which reads
 * the first argument as a card id — drew a bid as nothing at all.
 *
 * Which moves are questions is **declared** by the game, in its layout, and so
 * is what each of their arguments means. A renderer that sniffed ("does that
 * look like a card?") would be guessing per game, and would guess wrong the
 * first time one names a suit or a player.
 *
 * ## One band, chips, and the amount on demand
 *
 * Every legal spec used to render its OWN band, with its own padding, its own
 * background and its own label. Texas Hold'em has three legal at once and
 * spent 378px of a 584px phone column on them — three boxes to ask one
 * question — which left the hand and the table with less than nothing between
 * them and produced two regressions in a row as compensations. See
 * `docs/phone-column-plan.md` for the measurements.
 *
 * So there is one band now, and every spec is a chip inside it:
 *
 *   - no arguments            one chip, its label.            `[Fold]`
 *   - one answer              one chip, label and answer.     `[Call 20]`
 *   - two or more             the label, then a chip each.
 *   - an `amount`             one chip, which opens a slider. `[Raise to…]`
 *
 * Only the amount hides anything, and what it hides is a slider rather than a
 * choice. Fourteen bids wrap inside the band the way they always wrapped
 * inside their own: a row that scrolled sideways would hide the high bids,
 * which are the ones worth thinking about.
 *
 * ## Asking one argument at a time
 *
 * A move can carry more than one. Go Fish asks a named player for a rank, which
 * is two arguments, and one chip for every player-and-rank combination would be
 * dozens for a decision that is really two.
 *
 * So a multi-argument prompt offers the first argument in the row, and choosing
 * one swaps the band for the second question with a way back. The list always
 * comes from `legalMoves`, so a combination the server would refuse is never
 * offered — you cannot reach "ask Bob for a seven" unless the server said that
 * exact move was legal.
 */

import { useState } from 'react';
import {
  answersAt,
  argCount,
  clampToRange,
  presetsOf,
  promptDraws,
  promptShape,
  questionAt,
  rangeOf,
} from '../render/prompt-args.js';
import type { Answer, PromptSpec } from '../render/prompt-args.js';
import type { LegalMove } from '../net/match.js';
import type { GameLayoutWire } from '../net/session.js';
import type { SeatInfo } from '@cards/protocol';

/** A declared prompt and the moves the server is offering for it right now. */
interface Live {
  spec: PromptSpec;
  options: LegalMove[];
}

/**
 * The amount control, which is the one thing that does not fit in a row.
 *
 * Exported for its tests. It is what the band becomes once the `[Raise to…]`
 * chip is pressed, and the alternative to rendering it directly is a test that
 * has to click - which would mean a DOM for a question that is answered by
 * markup.
 *
 * The first input in this project that is not a choice from a list, because a
 * no-limit raise has thousands of legal answers and `enumerate` cannot send
 * them. The server sends the interval instead, so the client is still offered
 * exactly what will be accepted and composes nothing of its own.
 *
 * A slider alone cannot commit, so there is a button that says what it will do
 * — "Raise to 350" — and the presets are there because reaching for the pot or
 * for all-in is what people actually do, and dragging to them is not.
 */
export function RangeControl({
  spec,
  options,
  onMove,
  onBack,
}: {
  spec: PromptSpec;
  options: LegalMove[];
  onMove: (move: string, args: string[]) => void;
  onBack?: (() => void) | undefined;
}) {
  const range = rangeOf(options);
  const [amount, setAmount] = useState<number | null>(null);

  if (!range) return null;
  const chosen = amount === null ? range.min : clampToRange(range, amount);
  const presets = presetsOf(range);
  const fixed = range.min === range.max;

  return (
    <>
      {/*
        No label, because the button at the bottom is the label: "Raise to 240"
        says what this is and what pressing it does, and "How much?" above a
        slider above that button is a third way of saying it. Two rows of a
        259px band that has to fit in a 584px column - see the measurements in
        `docs/phone-column-plan.md`. The slider keeps the question as its
        accessible name, where it is not costing height.
      */}

      {/*
        Nothing to choose when the only legal amount is all-in, which is the
        common case for a short stack. A slider with one position is a worse
        way of saying that than not showing one.
      */}
      {fixed ? null : (
        <div className="prompt-range">
          <span className="prompt-range-end">{range.min}</span>
          <input
            type="range"
            min={range.min}
            max={range.max}
            step={range.step > 0 ? range.step : 1}
            value={chosen}
            aria-label={spec.label}
            onChange={(event) => setAmount(Number(event.target.value))}
          />
          <span className="prompt-range-end">{range.max}</span>
        </div>
      )}

      {/*
        The shortcuts, the commit and the way out, all on one line.

        They were three rows - presets, then commit, then back - and the panel
        that made was tall enough to squeeze the table behind it out of shape:
        the ring lost a hundred pixels, the board rode up into the far
        player's name, and the money had to be hidden to keep out of its way.
        Every one of those was the panel's height arriving somewhere else.

        One row also matches what the row of choices it replaced looked like,
        so opening an amount does not rearrange the bottom of the screen.
      */}
      <div className="prompt-foot">
        {presets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            className={preset.value === chosen ? 'prompt-choice chosen' : 'prompt-choice'}
            onClick={() => setAmount(preset.value)}
          >
            {preset.label}
          </button>
        ))}

        <button
          type="button"
          className="prompt-commit"
          onClick={() => {
            onMove(spec.move, [String(chosen)]);
            setAmount(null);
          }}
        >
          {`${spec.commit ?? 'Bet'} ${String(chosen)}`}
        </button>

        {onBack ? (
          <button type="button" className="text-button" onClick={onBack}>
            Back
          </button>
        ) : null}
      </div>
    </>
  );
}

/** One answer, drawn as a chip — a suit gets its glyph and its word. */
function Chip({
  answer,
  onPick,
}: {
  answer: Answer;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      className={answer.suit ? `prompt-choice suit${answer.red ? ' red' : ''}` : 'prompt-choice'}
      onClick={onPick}
      aria-label={answer.spoken}
    >
      {answer.suit ? (
        <>
          <span className="prompt-suit-mark" aria-hidden="true">
            {answer.suit.mark}
          </span>
          <span className="prompt-suit-word" aria-hidden="true">
            {answer.suit.word}
          </span>
        </>
      ) : (
        answer.shown
      )}
    </button>
  );
}

/**
 * Whether a label and its single answer read as one thing on one chip.
 *
 * "Call" and "20" do: the label is a verb and the answer is its object. "How
 * many tricks will you take?" and "0" do not, and a game whose only legal bid
 * is zero would otherwise get a chip with a question mark in the middle of it.
 *
 * A question ends in a question mark, which is the whole test. It is a
 * heuristic about English rather than a rule about the data, so it is written
 * here where it is visible rather than buried in the layout.
 */
function readsAsOneChip(label: string): boolean {
  return !label.trim().endsWith('?');
}

/** The chips for one declared prompt, in the shared row. */
function PromptGroup({
  live,
  seats,
  onMove,
  onOpen,
}: {
  live: Live;
  seats: SeatInfo[];
  onMove: (move: string, args: string[]) => void;
  onOpen: (move: string, chosen: string[]) => void;
}) {
  const { spec, options } = live;
  const shape = promptShape(spec, options);

  /*
   * A move that carries nothing is one chip, and the label IS the question.
   *
   * Handled before anything else because there is no argument to offer, so
   * asking for the answers at index 0 returns an empty list and the prompt
   * disappears — which is exactly what happened once: Cheat's "Do you believe
   * them?" and Rummy's two draws rendered as nothing at all, leaving a player
   * whose turn it was with no way to act. Both games declared it correctly; the
   * renderer was what stopped honouring it.
   */
  /*
   * The tone's class, or nothing.
   *
   * Built once and used by every shape below. A poker panel draws its moves
   * through three different branches - a plain button, a range that opens,
   * and a label-and-answer chip - so a tone applied in only one of them
   * would colour Check and leave Call grey.
   */
  const tone = spec.tone === undefined ? '' : ` tone-${spec.tone}`;

  if (shape === 'button') {
    return (
      <span className="prompt-group">
        <button
          type="button"
          className={`prompt-choice${tone}`}
          onClick={() => onMove(spec.move, [])}
          aria-label={spec.label}
        >
          {spec.label}
        </button>
      </span>
    );
  }

  /* An amount is the one answer a row cannot hold, so its chip opens it. */
  if (shape === 'range') {
    const verb = spec.commit ?? spec.label;
    return (
      <span className="prompt-group">
        <button
          type="button"
          className={`prompt-choice opens${tone}`}
          onClick={() => onOpen(spec.move, [])}
          aria-label={`${verb}: choose an amount`}
        >
          {`${verb}…`}
        </button>
      </span>
    );
  }

  const total = argCount(spec, options);
  const answers = answersAt(options, [], 0, spec, seats);
  if (answers.length === 0) return null;

  const pick = (value: string) => {
    if (total <= 1) {
      onMove(spec.move, [value]);
      return;
    }
    onOpen(spec.move, [value]);
  };

  /* "Call" and "20" are one thing to press, not a caption and a chip. */
  const only = answers[0] as Answer;
  if (total === 1 && answers.length === 1 && readsAsOneChip(spec.label)) {
    return (
      <span className="prompt-group">
        <button
          type="button"
          className={`prompt-choice${tone}`}
          onClick={() => pick(only.value)}
          aria-label={`${spec.label} ${only.spoken}`}
        >
          {`${spec.label} ${only.shown}`}
        </button>
      </span>
    );
  }

  return (
    <span className="prompt-group">
      <span className="prompt-label">{spec.label}</span>
      {answers.map((answer) => (
        <Chip key={answer.value} answer={answer} onPick={() => pick(answer.value)} />
      ))}
    </span>
  );
}

/** The band once a spec has taken it over: a later argument, or an amount. */
function Opened({
  live,
  chosen,
  seats,
  onMove,
  onBack,
  onAdvance,
}: {
  live: Live;
  chosen: string[];
  seats: SeatInfo[];
  onMove: (move: string, args: string[]) => void;
  onBack: () => void;
  onAdvance: (chosen: string[]) => void;
}) {
  const { spec, options } = live;
  const total = argCount(spec, options);
  const index = Math.min(chosen.length, Math.max(total - 1, 0));
  const answers = answersAt(options, chosen, index, spec, seats);

  const pick = (value: string) => {
    const next = [...chosen, value];
    /* The last argument sends the move; an earlier one asks the next
       question. No game declares three yet, and the day one does this is
       already the shape it needs. */
    if (next.length >= total) {
      onMove(spec.move, next);
      onBack();
      return;
    }
    onAdvance(next);
  };

  /*
   * Nothing left to offer means the band would draw an empty box.
   *
   * It cannot happen from a server that offered the move at all, which is why
   * this returns to the row rather than trying to say something about it.
   */
  if (answers.length === 0) return null;

  return (
    <>
      <span className="prompt-label">{questionAt(spec, chosen, seats)}</span>
      <div className="prompt-options">
        {answers.map((answer) => (
          <Chip key={answer.value} answer={answer} onPick={() => pick(answer.value)} />
        ))}
      </div>
    </>
  );
}

export function Prompts({
  layout,
  legalMoves,
  seats,
  onMove,
}: {
  layout: GameLayoutWire;
  legalMoves: LegalMove[];
  seats: SeatInfo[];
  onMove: (move: string, args: string[]) => void;
}) {
  const declared = (layout.prompts ?? []) as PromptSpec[];

  const live: Live[] = [];
  for (const spec of declared) {
    const options = legalMoves.filter((move) => move.move === spec.move);
    if (options.length > 0 && promptDraws(spec, options, seats)) live.push({ spec, options });
  }

  /*
   * Keyed by what is on offer, not only by the move names.
   *
   * A partly-answered prompt holds state, and that state is only valid for the
   * moves it was built from. Re-keying when the offer changes throws it away,
   * which is exactly right: "ask Bob" is meaningless once Bob is no longer in
   * the list. One key for the whole band now, rather than one per spec, because
   * the state moved up with the band.
   */
  const key = live
    .map((entry) => `${entry.spec.move}:${entry.options.map((o) => o.args.join(',')).join('|')}`)
    .join(';');

  if (live.length === 0) return null;
  return <PromptBand key={key} live={live} seats={seats} onMove={onMove} />;
}

function PromptBand({
  live,
  seats,
  onMove,
}: {
  live: Live[];
  seats: SeatInfo[];
  onMove: (move: string, args: string[]) => void;
}) {
  /*
   * Which spec has taken the band over, and what it has been told so far.
   *
   * Null for the ordinary case, which is every game that is not being asked
   * for an amount or for a second argument.
   */
  const [opened, setOpened] = useState<{ move: string; chosen: string[] } | null>(null);

  const open = opened === null ? null : (live.find((e) => e.spec.move === opened.move) ?? null);
  const back = () => setOpened(null);

  if (open && opened) {
    return (
      <div className="prompt">
        {promptShape(open.spec, open.options) === 'range' ? (
          /* Half-made choices are always reversible - nothing has been sent
             yet - so both of these carry a way back. The amount control puts
             it beside its commit button rather than under it. */
          <RangeControl
            spec={open.spec}
            options={open.options}
            onMove={(move, args) => {
              back();
              onMove(move, args);
            }}
            onBack={back}
          />
        ) : (
          <>
            <Opened
              live={open}
              chosen={opened.chosen}
              seats={seats}
              onMove={onMove}
              onBack={back}
              onAdvance={(chosen) => setOpened({ move: opened.move, chosen })}
            />
            <button type="button" className="text-button" onClick={back}>
              Back
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="prompt">
      <div className="prompt-row">
        {live.map((entry) => (
          <PromptGroup
            key={entry.spec.move}
            live={entry}
            seats={seats}
            onMove={onMove}
            onOpen={(move, chosen) => setOpened({ move, chosen })}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The move names a game has declared as questions.
 *
 * The hand uses this to keep them out of the card path: without it a bid of
 * seven is read as a card called "7", drawn into the fan, and tapped to play.
 */
export function promptMoves(layout: GameLayoutWire): Set<string> {
  const names = new Set<string>();
  for (const spec of layout.prompts ?? []) names.add(spec.move);
  return names;
}
